import { create } from "zustand";
import { devtools } from "zustand/middleware";
import APIs from "../application/APIs";
import { TemplateTypeEnum, TemplateResponse } from "../types/generatedTypes";

// Re-export generated types for consumers
export type TemplateType = TemplateTypeEnum;
export type DocumentTemplate = TemplateResponse;

interface FlowsTemplatesState {
  templates: DocumentTemplate[];
  templatesLoading: boolean;
  error: string | null;

  fetchTemplates: () => Promise<void>;
  createTemplate: (data: { name: string; template_type: TemplateType }) => Promise<string | null>;
  updateTemplate: (templateId: string, data: Partial<DocumentTemplate>) => Promise<void>;
  deleteTemplate: (templateId: string) => Promise<void>;
  uploadTemplateFile: (templateId: string, file: File) => Promise<void>;
  clearError: () => void;
}

const useFlowsTemplatesStore = create<FlowsTemplatesState>()(
  devtools(
    (set, get) => ({
      templates: [],
      templatesLoading: false,
      error: null,

      fetchTemplates: async () => {
        set({ templatesLoading: true, error: null });
        try {
          const response = await APIs.getFlowsAutomationsTemplates();
          set({ templates: response.templates, templatesLoading: false });
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          console.error("Failed to fetch templates:", error);
          set({ error: `Template fetch failed: ${message}`, templatesLoading: false });
        }
      },

      createTemplate: async (data) => {
        set({ error: null });
        try {
          const response = await APIs.createFlowsAutomationsTemplate(data);
          const newTemplate = response.data?.template;
          if (newTemplate) {
            set((state) => ({
              templates: [newTemplate, ...state.templates],
            }));
            return newTemplate.identifier;
          }
          return null;
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          console.error("Failed to create template:", error);
          set({ error: `Template creation failed: ${message}` });
          return null;
        }
      },

      updateTemplate: async (templateId, data) => {
        set({ error: null });
        try {
          await APIs.updateFlowsAutomationsTemplate(templateId, data);
          await get().fetchTemplates();
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          console.error("Failed to update template:", error);
          set({ error: `Template update failed: ${message}` });
        }
      },

      deleteTemplate: async (templateId) => {
        try {
          await APIs.deleteFlowsAutomationsTemplate(templateId);
          set((state) => ({
            templates: state.templates.filter((t) => t.identifier !== templateId),
          }));
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          console.error("Failed to delete template:", error);
          set({ error: `Template deletion failed: ${message}` });
        }
      },

      uploadTemplateFile: async (templateId, file) => {
        set({ error: null });
        try {
          await APIs.uploadFlowsAutomationsTemplateFile(templateId, file);
          await get().fetchTemplates();
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          console.error("Failed to upload template file:", error);
          set({ error: `Template file upload failed: ${message}` });
        }
      },

      clearError: () => set({ error: null }),
    }),
    { name: "flows-templates-store" }
  )
);

export const useFlowsTemplates = () => useFlowsTemplatesStore();

export default useFlowsTemplatesStore;
